import { useEffect, useRef, useState } from "react";
import { Upload, ImageOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { uploadImage } from "../../api/upload.api.js";
import { assetUrl, variantUrl } from "../../api/client.js";
import { cn } from "../../lib/cn.js";

/**
 * Single-image picker with preview. Uploads on selection and stores the
 * returned `url` (the original-format path; sharp variants live alongside).
 *
 *   <ImageUpload
 *     value={form.heroImage}
 *     onChange={(url) => form.setValue("heroImage", url)}
 *     subdir="designs"
 *     label="Hero image"
 *   />
 *
 * The preview prefers an in-memory blob URL the moment a file is picked, so
 * users see their image instantly without waiting on the server round-trip
 * (and without hitting `/uploads/...` over the network for the same bytes).
 * The server URL still becomes the persisted `value`; blob URLs are
 * session-only and revoked on unmount.
 */
export function ImageUpload({
  value,
  onChange,
  subdir,
  label,
  hint,
  required,
  aspect = "video",
  className,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [loadError, setLoadError] = useState(false);

  // Revoke any outstanding blob URL when the component unmounts or the
  // preview is replaced. Without this we leak memory for every file picked.
  useEffect(() => {
    return () => {
      if (localPreview?.startsWith("blob:")) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  // If the parent clears `value` externally (e.g. form reset), drop our local
  // blob preview too so the empty-state UI returns.
  useEffect(() => {
    if (!value && localPreview && !uploading) {
      if (localPreview.startsWith("blob:")) URL.revokeObjectURL(localPreview);
      setLocalPreview(null);
    }
  }, [value, localPreview, uploading]);

  const handleFile = async (file) => {
    if (!file) return;
    if (localPreview?.startsWith("blob:")) URL.revokeObjectURL(localPreview);
    const blobUrl = URL.createObjectURL(file);
    setLocalPreview(blobUrl);
    setLoadError(false);
    setUploading(true);
    try {
      const result = await uploadImage(file, subdir);
      onChange(result.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Upload failed");
      URL.revokeObjectURL(blobUrl);
      setLocalPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    if (localPreview?.startsWith("blob:")) URL.revokeObjectURL(localPreview);
    setLocalPreview(null);
    setLoadError(false);
    onChange("");
    if (inputRef.current) inputRef.current.value = "";
  };

  // Prefer the in-memory blob (paints instantly) while it exists; otherwise
  // resolve the server-stored path or external stock URL.
  const previewSrc = localPreview ?? (value ? assetUrl(value) : "");
  const hasPreview = Boolean(previewSrc);

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label className="block text-xs font-medium uppercase tracking-wider text-text-secondary">
          {label}
          {required && <span className="ml-1 text-danger">*</span>}
        </label>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div
        className={cn(
          "relative flex items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-border-subtle bg-bg-elevated-2 transition-colors hover:border-accent",
          {
            "aspect-video": aspect === "video",
            "aspect-square": aspect === "square",
            "aspect-[4/3]": aspect === "4/3",
          }
        )}
      >
        {hasPreview ? (
          <>
            {!loadError ? (
              <img
                src={previewSrc}
                alt=""
                className="h-full w-full object-cover"
                onError={() => setLoadError(true)}
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-text-muted">
                <AlertTriangle className="h-6 w-6 text-danger" />
                <span className="px-4 text-center text-xs">
                  Couldn&apos;t load preview. The file is saved — try refreshing or re-uploading.
                </span>
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-bg-base/40 backdrop-blur-sm">
                <span className="flex items-center gap-2 rounded-full bg-bg-base/80 px-3 py-1.5 text-xs text-text-primary">
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Uploading…
                </span>
              </div>
            )}
            <div className="absolute inset-0 flex items-end justify-end gap-2 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 transition-opacity hover:opacity-100">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="rounded-md bg-bg-base/80 px-3 py-1.5 text-xs text-text-primary backdrop-blur hover:bg-bg-base disabled:opacity-50"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading}
                className="rounded-md bg-danger/80 px-3 py-1.5 text-xs text-white backdrop-blur hover:bg-danger disabled:opacity-50"
              >
                Remove
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="flex h-full w-full flex-col items-center justify-center gap-2 text-text-muted hover:text-accent"
          >
            {uploading ? (
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Upload className="h-6 w-6" />
            )}
            <span className="text-xs uppercase tracking-wider">
              {uploading ? "Uploading…" : "Click to upload"}
            </span>
          </button>
        )}
      </div>
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

export function ImageThumbnail({ src, alt = "", size = "md", className, onClick }) {
  const dim = size === "sm" ? "h-10 w-10" : size === "lg" ? "h-20 w-20" : "h-14 w-14";
  return (
    <div
      onClick={onClick}
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded border border-border-subtle bg-bg-elevated-2",
        dim,
        onClick && "cursor-pointer",
        className
      )}
    >
      {src ? (
        <img
          // Prefer the WebP `-thumb` variant — typically ~10 KB versus a
          // multi-MB original, which keeps list pages snappy.
          src={size === "lg" ? assetUrl(src) : variantUrl(src, "thumbnail")}
          alt={alt}
          className="h-full w-full object-cover"
          onError={(e) => {
            // If the thumbnail variant is missing (older uploads), fall back
            // to the original before giving up.
            if (!e.currentTarget.dataset.fellBackToOriginal) {
              e.currentTarget.dataset.fellBackToOriginal = "1";
              e.currentTarget.src = assetUrl(src);
              return;
            }
            e.currentTarget.style.display = "none";
            e.currentTarget.parentElement
              ?.querySelector("[data-fallback]")
              ?.classList.remove("hidden");
          }}
        />
      ) : null}
      <ImageOff data-fallback className={cn("h-4 w-4 text-text-muted", src && "hidden")} />
    </div>
  );
}
